import { createFileRoute } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import { CheckCircle2, AlertCircle, FileText, Calendar, User } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Skeleton } from '@/components/atoms/skeleton';
import { Separator } from '@/components/atoms/separator';
import { PoweredByNotifi } from '@/components/public/PoweredByNotifi';

import { useSignedPageData } from '@/hooks/payment/useSignedPageData';
import { PublicCenteredPage } from '@/components/public/PublicCenteredPage';

export const Route = createFileRoute('/(public)/signed/$signedId')({
  component: SignedPage,
});

function SignedPage() {
  const { signedId } = Route.useParams();
  const { data, isLoading, isError } = useSignedPageData(signedId);

  if (isLoading) {
    return (
      <PublicCenteredPage>
        <Card className="w-full max-w-md">
          <CardHeader className="items-center text-center space-y-2">
            <Skeleton className="h-16 w-16 rounded-full" />
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-20 w-full rounded-lg" />
            <Skeleton className="h-20 w-full rounded-lg" />
          </CardContent>
        </Card>
      </PublicCenteredPage>
    );
  }

  if (isError || !data) {
    return (
      <PublicCenteredPage>
        <Card className="w-full max-w-md text-center p-8">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-lg font-semibold">Signature not found</h2>
          <p className="text-muted-foreground text-sm mt-2">
            This signature confirmation may be expired or the link is invalid.
          </p>
        </Card>
      </PublicCenteredPage>
    );
  }

  const signedAt = parseISO(data.signedAt);

  return (
    <PublicCenteredPage>
      {/* Branding */}
      <header className="mb-6 text-center">
        {data.companyLogoUrl ? (
          <img
            src={data.companyLogoUrl}
            alt={data.companyName}
            className="h-12 mx-auto object-contain"
            loading="lazy"
          />
        ) : data.companyName ? (
          <h1 className="text-xl font-bold text-foreground">{data.companyName}</h1>
        ) : null}
      </header>

      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center space-y-3 pb-4">
          <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <div>
            <CardTitle className="text-xl">Document Signed</CardTitle>
            <p className="text-muted-foreground text-sm mt-1">
              Your digital signature has been recorded successfully.
            </p>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Document details */}
          <div className="rounded-xl bg-muted/40 border border-border p-4 space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <FileText className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">Document</p>
                <p className="font-medium text-foreground break-words">{data.documentTitle}</p>
              </div>
            </div>

            <Separator />

            <div className="flex items-start gap-3">
              <User className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">Signed by</p>
                <p className="font-medium text-foreground">{data.signerName ?? 'Customer'}</p>
              </div>
            </div>

            <Separator />

            <div className="flex items-start gap-3">
              <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">Signed on</p>
                <p className="font-medium text-foreground">
                  {format(signedAt, "MMMM d, yyyy 'at' h:mm a")}
                </p>
              </div>
            </div>

            {data.referenceNumber && (
              <>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Reference #</span>
                  <span className="font-mono font-semibold text-foreground">
                    {data.referenceNumber}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Signature verification badge */}
          <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30 px-4 py-3">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
            <p className="text-xs text-green-700 dark:text-green-400">
              This document has been digitally signed and verified. A copy has been sent to your
              email.
            </p>
          </div>

          <div className="pt-1 text-center">
            <PoweredByNotifi />
          </div>
        </CardContent>
      </Card>
    </PublicCenteredPage>
  );
}
